import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Link, useNavigate } from "react-router-dom"
import { Camera, ChevronRight, FileText, FileUp, Loader2, Mail, Search, UploadCloud } from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import {
  createReceipt,
  eurFmt,
  listReceipts,
  ODOO_STATE_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  uploadFile,
  type ReceiptListItem,
  type ReceiptOrigin,
  type ReceiptStatus,
} from "./api"
import { useMinWidth } from "./ui"

export const MAX_FILE_BYTES = 20 * 1024 * 1024

type Filter = "acao" | "a_rever" | "prontas" | "escritas" | "erro" | "todas"

/** O que precisa de ação: a rever, prontas, com erro e as confirmadas a meio. */
const NEEDS_ACTION: ReceiptStatus[] = ["a_extrair", "a_rever", "pronto", "erro", "erro_escrita", "confirmado"]

const FILTERS: Array<{ key: Filter; label: string; match: (s: ReceiptStatus) => boolean }> = [
  { key: "acao", label: "Por tratar", match: (s) => NEEDS_ACTION.includes(s) },
  { key: "a_rever", label: "A rever", match: (s) => s === "a_rever" },
  { key: "prontas", label: "Prontas", match: (s) => s === "pronto" },
  { key: "escritas", label: "Escritas no Odoo", match: (s) => s === "escrito" || s === "concluida_manual" },
  { key: "erro", label: "Com erro", match: (s) => s === "erro" || s === "erro_escrita" },
  { key: "todas", label: "Todas", match: () => true },
]

const fmtDate = (iso: string | null) => (iso ? iso.split("-").reverse().join("/") : "—")

/**
 * Receções (Fase 2d): título, área de carregar a toda a largura (no telemóvel,
 * «Tirar foto» e «Escolher ficheiro») e um cartão com pesquisa, filtro de
 * estado com contagens e a tabela — cartões abaixo de 768 px.
 */
export function ReceiptsList({ shell }: { shell: AppShell }) {
  const navigate = useNavigate()
  const table = useMinWidth(768)
  const [items, setItems] = useState<ReceiptListItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<Filter>("acao")

  const load = () =>
    listReceipts(shell)
      .then(setItems)
      .catch((e: Error) => setError(e.message))

  useEffect(() => {
    let alive = true
    load()
    // Enquanto houver documentos a ler ou a escrever, a lista atualiza-se sozinha.
    const timer = setInterval(() => {
      if (alive && items?.some((i) => i.status === "a_extrair" || i.status === "confirmado")) load()
    }, 10_000)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [shell, items?.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items ?? []
    return (items ?? []).filter((r) =>
      [r.receipt_code, r.supplier_name, r.supplier_nif, r.document_number, r.document_type, ...r.order_names]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    )
  }, [items, query])
  const active = FILTERS.find((f) => f.key === filter)!
  const shown = searched.filter((r) => active.match(r.status))

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Receções</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Carregue a fatura ou a guia do fornecedor: os artigos e os números de série são lidos e conferidos com a encomenda, e depois de
          confirmados são escritos no Odoo.
        </p>
      </div>

      <UploadArea shell={shell} desktop={table} onCreated={(ids) => (ids.length === 1 ? navigate(`/armazem/${ids[0]}`) : load())} />

      <div className="rounded-xl border bg-card">
        <div className="space-y-3 border-b p-3 sm:p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Fornecedor, NIF, nº do documento ou encomenda"
              aria-label="Pesquisar receções"
              className="h-12 w-full rounded-lg border bg-background pl-10 pr-3 text-base sm:h-10 sm:text-sm"
            />
          </div>
          {/* No telemóvel os filtros deslizam numa faixa (a página não). */}
          <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0" role="tablist" aria-label="Estado">
            {FILTERS.map((f) => {
              const n = searched.filter((r) => f.match(r.status)).length
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    "inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-medium transition-colors sm:h-8",
                    filter === f.key ? "border-primary bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted",
                  )}
                >
                  {f.label}
                  <span className={cn("tabular-nums", filter === f.key ? "opacity-80" : "text-foreground/60")}>({n})</span>
                </button>
              )
            })}
          </div>
        </div>

        {error && <p className="p-4 text-sm text-red-600">{error}</p>}
        {items === null && !error && <p className="p-4 text-sm text-muted-foreground">A carregar…</p>}
        {items !== null && shown.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {items.length === 0 ? "Ainda não há receções. Carregue a primeira fatura acima." : "Nenhuma receção com este filtro."}
          </p>
        )}

        {shown.length > 0 &&
          (table ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b">
                    <th className="px-3 py-2.5 lg:px-4">Código</th>
                    <th className="px-2.5 py-2.5">Estado</th>
                    <th className="px-2.5 py-2.5">Fornecedor</th>
                    <th className="px-2.5 py-2.5">Documento</th>
                    <th className="hidden px-2.5 py-2.5 lg:table-cell">Encomenda</th>
                    <th className="hidden px-2.5 py-2.5 xl:table-cell">Data</th>
                    <th className="hidden px-2.5 py-2.5 xl:table-cell">Séries</th>
                    <th className="px-2.5 py-2.5 text-right">Total</th>
                    <th className="hidden px-2.5 py-2.5 xl:table-cell">Odoo</th>
                    <th className="px-2.5 py-2.5 text-center lg:px-4">Origem</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/40" onClick={() => navigate(`/armazem/${r.id}`)}>
                      <td className="whitespace-nowrap px-3 py-3 font-mono text-[13px] lg:px-4">
                        <Link to={`/armazem/${r.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
                          {r.receipt_code ?? "—"}
                        </Link>
                      </td>
                      <td className="px-2.5 py-3">
                        <Badge variant={STATUS_VARIANT[r.status]} className="whitespace-nowrap">
                          {STATUS_LABEL[r.status]}
                        </Badge>
                      </td>
                      <td className="max-w-[10rem] px-2.5 py-3 lg:max-w-[13rem]">
                        <div className="truncate font-medium">{r.supplier_name ?? "Fornecedor por ler"}</div>
                        {r.supplier_nif && <div className="text-xs text-muted-foreground">NIF {r.supplier_nif}</div>}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-3">
                        {docLabel(r)}
                        {/* Sem espaço para a coluna Séries (768–1279): vai por baixo do documento. */}
                        {r.serial_count > 0 && <div className="text-xs text-muted-foreground xl:hidden">{seriesLabel(r)}</div>}
                      </td>
                      <td className="hidden max-w-[8rem] px-2.5 py-3 lg:table-cell">{r.order_names.length ? r.order_names.join(", ") : "—"}</td>
                      <td className="hidden whitespace-nowrap px-2.5 py-3 tabular-nums xl:table-cell">{fmtDate(r.document_date)}</td>
                      <td className="hidden whitespace-nowrap px-2.5 py-3 text-muted-foreground xl:table-cell">{seriesLabel(r)}</td>
                      <td className="whitespace-nowrap px-2.5 py-3 text-right tabular-nums">{eurFmt(r.total)}</td>
                      <td className={cn("hidden whitespace-nowrap px-2.5 py-3 xl:table-cell", r.odoo_state === "erro" && "text-red-600", r.odoo_state === "por_validar" && "text-amber-700")}>
                        {ODOO_STATE_LABEL[r.odoo_state]}
                      </td>
                      <td className="px-2.5 py-3 text-center lg:px-4">
                        <OriginIcon origin={r.origin} detail={r.origin_detail} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <ul className="divide-y">
              {shown.map((r) => (
                <li key={r.id}>
                  <Link to={`/armazem/${r.id}`} className="flex items-center gap-3 px-2.5 py-3 active:bg-muted/60">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex items-start gap-2">
                        <span className="min-w-0 flex-1 font-medium leading-snug">{r.supplier_name ?? "Fornecedor por ler"}</span>
                        <Badge variant={STATUS_VARIANT[r.status]} className="shrink-0 text-sm">
                          {STATUS_LABEL[r.status]}
                        </Badge>
                      </div>
                      <div className="flex items-baseline gap-2 text-sm">
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-mono text-muted-foreground">{r.receipt_code ?? ""}</span> {docLabel(r)}
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">{eurFmt(r.total)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="min-w-0 flex-1 truncate">
                          {[seriesLabel(r), r.order_names.join(", ")].filter((x) => x && x !== "—").join(" · ") || "—"}
                        </span>
                        <OriginIcon origin={r.origin} detail={r.origin_detail} />
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          ))}
      </div>
    </div>
  )
}

/** Tipo e número (`FT 2026A12/3389`); o número muitas vezes já traz o tipo. */
export function docLabel(r: { document_type: string | null; document_number: string | null }): string {
  const n = (r.document_number ?? "").trim()
  const t = (r.document_type ?? "").trim()
  if (!n) return "—"
  return t && !n.toUpperCase().startsWith(t.toUpperCase()) ? `${t} ${n}` : n
}

function seriesLabel(r: ReceiptListItem): string {
  if (r.serial_count === 0) return "—"
  return `${r.serial_count} série${r.serial_count === 1 ? "" : "s"} · ${r.article_count} artigo${r.article_count === 1 ? "" : "s"}`
}

export function OriginIcon({ origin, detail }: { origin: ReceiptOrigin | null; detail: string | null }) {
  const label = origin === "email" ? `Email${detail ? ` de ${detail}` : ""}` : origin === "foto" ? "Foto" : origin === "ficheiro" ? "Ficheiro" : "—"
  const Icon = origin === "email" ? Mail : origin === "foto" ? Camera : FileText
  return (
    <span title={label} aria-label={label} className="inline-flex h-6 w-6 items-center justify-center text-muted-foreground">
      <Icon className="h-4 w-4" />
    </span>
  )
}

/**
 * Carregar faturas. Computador: arrastar ou clicar; com vários ficheiros a app
 * pergunta se são **uma fatura com várias páginas** (vão para o ecrã das
 * páginas) ou **várias faturas** (um documento por ficheiro). Telemóvel:
 * «Tirar foto» e «Escolher ficheiro» abrem o ecrã das páginas, como na 2c.
 */
function UploadArea({ shell, desktop, onCreated }: { shell: AppShell; desktop: boolean; onCreated: (ids: string[]) => void }) {
  const navigate = useNavigate()
  const picker = useRef<HTMLInputElement>(null)
  const camera = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Vários ficheiros no computador: à espera de «uma fatura» ou «várias». */
  const [choose, setChoose] = useState<File[] | null>(null)

  const toPages = (list: FileList | null, fromCamera: boolean) => {
    if (!list?.length) return
    navigate("/armazem/nova", { state: { files: Array.from(list), fromCamera } })
  }

  /** Com vários ficheiros, primeiro pergunta; com um, carrega logo. */
  const pick = (list: FileList | File[] | null) => {
    const files = Array.from(list ?? []).filter((f) => f.type.startsWith("image/") || f.type === "application/pdf")
    if (files.length === 0) return setError("Só PDF ou imagens.")
    const big = files.find((f) => f.size > MAX_FILE_BYTES)
    if (big) return setError(`${big.name} tem mais de 20 MB.`)
    setError(null)
    if (files.length > 1) return setChoose(files)
    void uploadEach(files)
  }

  const uploadEach = async (files: File[]) => {
    setChoose(null)
    setError(null)
    setProgress({ done: 0, total: files.length })
    const ids: string[] = []
    try {
      for (const f of files) {
        const key = await uploadFile(shell, f)
        const origin = f.type.startsWith("image/") ? "foto" : "ficheiro"
        ids.push((await createReceipt(shell, [key], origin)).id)
        setProgress({ done: ids.length, total: files.length })
      }
      onCreated(ids)
    } catch (e) {
      setError((e as Error).message)
      // As que já foram criadas aparecem na lista.
      if (ids.length) onCreated([])
    } finally {
      setProgress(null)
    }
  }

  if (!desktop) {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => camera.current?.click()}
            className="flex h-[76px] flex-col items-center justify-center gap-1 rounded-xl bg-primary text-base font-semibold text-primary-foreground active:opacity-90"
          >
            <Camera className="h-6 w-6" /> Tirar foto
          </button>
          <button
            type="button"
            onClick={() => picker.current?.click()}
            className="flex h-[76px] flex-col items-center justify-center gap-1 rounded-xl border bg-card text-base font-semibold active:bg-muted"
          >
            <FileUp className="h-6 w-6" /> Escolher ficheiro
          </button>
        </div>
        <p className="text-center text-sm text-muted-foreground">PDF ou imagem, até 20 MB. Várias páginas? Junte uma foto por página.</p>
        <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => (toPages(e.target.files, true), (e.target.value = ""))} />
        <input ref={picker} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => (toPages(e.target.files, false), (e.target.value = ""))} />
      </div>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => picker.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          pick(e.dataTransfer.files)
        }}
        disabled={progress !== null || choose !== null}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors",
          over ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/40",
        )}
      >
        {progress ? (
          <>
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            <span className="text-sm font-medium">
              A enviar {progress.done + 1 > progress.total ? progress.total : progress.done + 1} de {progress.total}…
            </span>
          </>
        ) : (
          <>
            <UploadCloud className="h-7 w-7 text-muted-foreground" />
            <span className="text-sm font-medium">Arraste faturas para aqui ou clique para escolher</span>
            <span className="text-xs text-muted-foreground">PDF ou imagem, até 20 MB — com vários ficheiros, a app pergunta se são páginas da mesma fatura</span>
          </>
        )}
      </button>
      {choose && (
        <div className="mt-2 space-y-3 rounded-xl border border-border bg-card p-4">
          <p className="text-sm font-medium">{choose.length} ficheiros escolhidos. São…</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate("/armazem/nova", { state: { files: choose, fromCamera: false } })}>
              Uma fatura com {choose.length} páginas
            </Button>
            <Button variant="outline" onClick={() => void uploadEach(choose)}>
              {choose.length} faturas, uma por ficheiro
            </Button>
            <Button variant="ghost" onClick={() => setChoose(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <input ref={picker} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => (pick(e.target.files), (e.target.value = ""))} />
    </div>
  )
}
